/**
 * The website Make changes session — the same contract as the transaction
 * mode's `useCustomizeSession` (page-runtime `CustomizeSession`), so the
 * shared violet bar shows Saving… / Saved / Not saved, Undo / Redo with their
 * labels and the refusal line unchanged.
 *
 * What differs is what a change IS. A website change stores a whole page or
 * the site's settings (`/api/site-edit`), so a change is recorded as the
 * stored action plus the action that stores what was there before: Undo runs
 * the second, Redo the first. A refused step stays on its stack and can be
 * tried again, exactly like the transaction session.
 */

import { useCallback, useMemo, useRef, useState } from 'react'
import { policyErrors } from '@twasta/page-runtime/customize'
import type { CustomizeSession, CustomizeStatus } from '@twasta/page-runtime/customize'

interface Entry {
  label: string
  undo: () => Promise<void>
  redo: () => Promise<void>
}

export interface SiteEditSession extends CustomizeSession {
  /** Store a change: `run` does it now (and is what Redo repeats), `revert`
   *  undoes it. Resolves true when stored, false when refused. */
  record: (label: string, run: () => Promise<void>, revert: () => Promise<void>) => Promise<boolean>
  /** Report a failure that is not a recorded change (a panel that could not
   *  load, a page that could not be added). */
  fail: (e: unknown) => void
}

export function useSiteEditSession(objName: string): SiteEditSession {
  const [status, setStatus] = useState<CustomizeStatus>('idle')
  const [errors, setErrors] = useState<string[]>([])
  const [undoStack, setUndoStack] = useState<Entry[]>([])
  const [redoStack, setRedoStack] = useState<Entry[]>([])
  const [changeLog, setChangeLog] = useState<string[]>([])
  // One step at a time: a second click while a save is in flight would
  // compute its "before" from a page the server has not stored yet.
  const busy = useRef(false)
  const undoRef = useRef<Entry[]>([])
  const redoRef = useRef<Entry[]>([])
  undoRef.current = undoStack
  redoRef.current = redoStack

  const fail = useCallback((e: unknown) => {
    console.warn('[site-edit] change refused', e)
    setErrors(policyErrors(e))
    setStatus('error')
  }, [])

  const record = useCallback(async (label: string, run: () => Promise<void>, revert: () => Promise<void>) => {
    if (busy.current) return false
    busy.current = true
    setStatus('saving')
    setErrors([])
    try {
      await run()
      setUndoStack((s) => [...s, { label, undo: revert, redo: run }].slice(-100))
      setRedoStack([])
      setChangeLog((l) => [...l, label])
      setStatus('saved')
      return true
    } catch (e) {
      fail(e)
      return false
    } finally {
      busy.current = false
    }
  }, [fail])

  const step = useCallback(async (dir: 'undo' | 'redo') => {
    if (busy.current) return
    const from = dir === 'undo' ? undoRef.current : redoRef.current
    const entry = from[from.length - 1]
    if (!entry) return
    busy.current = true
    setStatus('saving')
    setErrors([])
    try {
      await (dir === 'undo' ? entry.undo() : entry.redo())
      if (dir === 'undo') {
        setUndoStack((s) => s.slice(0, -1))
        setRedoStack((s) => [...s, entry])
        setChangeLog((l) => {
          const i = l.lastIndexOf(entry.label)
          return i < 0 ? l : [...l.slice(0, i), ...l.slice(i + 1)]
        })
      } else {
        setRedoStack((s) => s.slice(0, -1))
        setUndoStack((s) => [...s, entry])
        setChangeLog((l) => [...l, entry.label])
      }
      setStatus('saved')
    } catch (e) {
      fail(e)
    } finally {
      busy.current = false
    }
  }, [fail])

  return useMemo<SiteEditSession>(() => ({
    objType: 'T',
    objName,
    status,
    errors,
    overlay: {},
    effective: null,
    core: null,
    apply: async () => false,
    undo: () => step('undo'),
    canUndo: undoStack.length > 0,
    undoLabel: undoStack[undoStack.length - 1]?.label || '',
    redo: () => step('redo'),
    canRedo: redoStack.length > 0,
    redoLabel: redoStack[redoStack.length - 1]?.label || '',
    resetUnder: async () => false,
    resetAll: async () => false,
    isCustomized: () => false,
    countCustomized: () => 0,
    dismissErrors: () => { setErrors([]); setStatus('idle') },
    changeLog,
    warnings: [],
    record,
    fail,
  }), [objName, status, errors, undoStack, redoStack, changeLog, step, record, fail])
}
